import React, { useState } from 'react';
import { Dropdown, Badge, Spinner } from 'react-bootstrap';
import { FaBell, FaCircle, FaCheckDouble, FaSync } from '../../../icons';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { NotificationDropdownProps, NotificationItem } from './types/notification.types';
import './NotificationDropdown.scss';

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  notifications,
  unreadCount,
  loading,
  onMarkAsRead,
  onMarkAllAsRead,
  onRefresh
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const navigate = useNavigate();

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await onRefresh();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const getNotificationIcon = (type: NotificationItem['type']) => {
    switch (type) {
      case 'success':
        return <FaCircle className="notification-icon success" />;
      case 'warning':
        return <FaCircle className="notification-icon warning" />;
      case 'error':
        return <FaCircle className="notification-icon error" />;
      default:
        return <FaCircle className="notification-icon info" />;
    }
  };

  const formatNotificationTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return 'Unknown time';
    }
  };

  const handleNotificationClick = (notification: NotificationItem) => {

    // Mark as read if unread
    if (!notification.isRead) {
      onMarkAsRead(notification.id);
    }

    // Determine navigation route based on notification type or content
    let targetRoute = '/';
    let ticketId = null;

    // Check if it's a support ticket notification
    if (notification.ticketId || notification.relatedTicketId) {
      ticketId = notification.ticketId || notification.relatedTicketId;
      targetRoute = `/help-support/ticket/${ticketId}`;
    }
    // Check if message mentions ticket or support
    else if (notification.message?.toLowerCase().includes('ticket') ||
             notification.message?.toLowerCase().includes('support')) {

      // More comprehensive ticket ID extraction patterns
      const patterns = [
        /ticket[:\s#]*([a-f0-9]{24})/i,  // ticket: 68cbd1128625ca837ebe0cad
        /ticket[:\s#]*([a-f0-9]{12,})/i, // ticket: shorter IDs
        /id[:\s#]*([a-f0-9]{24})/i,      // id: 68cbd1128625ca837ebe0cad
        /([a-f0-9]{24})/i,               // just the 24-char hex ID
      ];

      for (const pattern of patterns) {
        const match = notification.message.match(pattern);
        if (match) {
          ticketId = match[1];
          targetRoute = `/help-support/ticket/${ticketId}`;
          break;
        }
      }

      if (!ticketId) {
        targetRoute = '/help-support';
      }
    }
    // Check if the notification ID itself might be a ticket ID (if it's about tickets)
    else if ((notification.title?.toLowerCase().includes('ticket') ||
             notification.title?.toLowerCase().includes('support')) &&
             notification.id.length === 24) {
      ticketId = notification.id;
      targetRoute = `/help-support/ticket/${ticketId}`;
    }
    // Check if it's a gift-related notification
    else if (notification.giftId || notification.relatedGiftId) {
      targetRoute = '/my-gifts';
    }
    // Check if it's about contacts
    else if (notification.message?.toLowerCase().includes('contact')) {
      targetRoute = '/my-contacts';
    }
    // Default fallback based on notification type
    else {
      switch (notification.type) {
        case 'success':
        case 'info':
          targetRoute = '/my-gifts';
          break;
        case 'warning':
        case 'error':
          targetRoute = '/help-support';
          break;
        default:
          targetRoute = '/';
      }
    }

    navigate(targetRoute);
  };

  return (
    <Dropdown className="notification-dropdown">
      <Dropdown.Toggle variant="transparent" className="notification-toggle">
        <div className="notification-bell">
          <FaBell size={20} />
          {unreadCount > 0 && (
            <Badge bg="danger" className="notification-badge">
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </div>
      </Dropdown.Toggle>

      <Dropdown.Menu className="notification-menu pt-0">
        <div className="notification-header">
          <h6 className="notification-title">
            Notifications
            {loading && <Spinner animation="border" size="sm" className="ms-2" />}
          </h6>
          <div className="notification-actions">
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={isRefreshing}
              title="Refresh notifications"
            >
              <FaSync className={isRefreshing ? 'spinning' : ''} />
            </button>
            {unreadCount > 0 && (
              <button
                className="btn-mark-all"
                onClick={onMarkAllAsRead}
                title="Mark all as read"
              >
                <FaCheckDouble />
              </button>
            )}
          </div>
        </div>

        <div className="notification-list">
          {loading && notifications.length === 0 ? (
            <div className="notification-loading">
              <Spinner animation="border" size="sm" />
              <span>Loading notifications...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="notification-empty">
              <FaBell size={24} />
              <p>No notifications yet</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
                onClick={() => handleNotificationClick(notification)}
                style={{ cursor: 'pointer' }}
              >
                <div className="notification-content">
                  <div className="notification-header-item">
                    {getNotificationIcon(notification.type)}
                    <span className="notification-item-title">{notification.title}</span>
                    {!notification.isRead && <div className="unread-indicator" />}
                  </div>
                  <p className="notification-message">{notification.message}</p>
                  <span className="notification-time">
                    {formatNotificationTime(notification.createdAt)}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {notifications.length > 0 && (
          <div className="notification-footer">
            <small className="text-muted">
              {unreadCount > 0
                ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`
                : 'All notifications read'
              }
            </small>
          </div>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default NotificationDropdown;